import clsx from "clsx";
import { FunctionComponent } from "react";

import {
  getTeamLogoUrlByCommunityName,
  hasTeamLogoOverride,
  MEDIA_BASE_URL,
  cleanCommunityLogo,
} from "@ui/components/brand/sbnation/utils/logo.utils";
import Image from "@ui/components/media/Image/Image";
import {
  AnyNotification,
  AnyCoralNotification,
  AnyCycloneNotification,
  NotificationSource,
} from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./CommunityLogo.css";

const COMMUNITY_LOGO_SIZES = { small: 30, large: 50 };

const COMMUNITY_NAME_OVERRIDES: Record<string, string> = {
  "SBNation Product": `${MEDIA_BASE_URL}/logo-sbnation.png`,
  "March Madness": `${MEDIA_BASE_URL}/logo-sbnation.png`,
  NFL: `${MEDIA_BASE_URL}/logo-sbnation.png`,
  NBA: `${MEDIA_BASE_URL}/logo-sbnation.png`,
};

const SITE_NAME_OVERRIDES: Record<string, string> = {
  "The Verge": `/static-assets/icons/default-headshot.png`,
};

const getCommunityInfo = (notification: AnyNotification, logoSize: number) => {
  switch (notification.source) {
    case NotificationSource.Cyclone: {
      const cycloneN = notification as AnyCycloneNotification;
      let logo: string = cleanCommunityLogo(cycloneN.community.logo);
      if (
        cycloneN.community.name &&
        cycloneN.community.name in COMMUNITY_NAME_OVERRIDES
      ) {
        logo = COMMUNITY_NAME_OVERRIDES[cycloneN.community.name] as string;
      } else if (
        cycloneN.community.name &&
        hasTeamLogoOverride(cycloneN.community.name)
      ) {
        logo = getTeamLogoUrlByCommunityName({
          communityName: cycloneN.community.name,
          width: logoSize,
        });
      }
      return { logo, name: cycloneN.community.name };
    }
    case NotificationSource.Coral: {
      const coralN = notification as AnyCoralNotification;
      let logo: string = "";
      const siteNameKey = Object.keys(SITE_NAME_OVERRIDES).find((key) =>
        coralN.site.name.includes(key),
      );
      if (siteNameKey) {
        logo = SITE_NAME_OVERRIDES[siteNameKey] as string;
      } else {
        logo = getTeamLogoUrlByCommunityName({
          communityName: coralN.site.name,
          width: logoSize,
        });
      }

      return {
        logo,
        name: coralN.site.name,
      };
    }
  }
};

interface CommunityLogoProps {
  notification: AnyNotification;
  size?: "small" | "large";
  alignment?: "bottom-right";
  needsWrapper?: boolean;
}

const CommunityLogo: FunctionComponent<CommunityLogoProps> = ({
  notification,
  size = "small",
  alignment,
  needsWrapper = false,
}) => {
  const logoSize = COMMUNITY_LOGO_SIZES[size];

  const { logo, name } = getCommunityInfo(notification, logoSize);

  const LogoImage = (
    <Image
      src={logo}
      alt={name}
      className={clsx(
        styles.communityLogoImage[size],
        alignment && styles.alignment[alignment],
      )}
      width={logoSize}
      height={logoSize}
    />
  );
  return needsWrapper ? (
    <div className={styles.communityLogoWrapper}>{LogoImage}</div>
  ) : (
    LogoImage
  );
};

export default CommunityLogo;
